import { useState } from 'react'
import { CryptoCode, FiatCode } from '@/constants/currencyCodes'
import { useCreatePurchaseOnRampLink } from '@/domain/transfer'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

export interface GetMessageProps {
  fiatValue: string
  currencyValue: string
  currencyCode: string
  fiatCode: FiatCode
  userId: string | undefined
  priceQuotesId: string | undefined
}

export function useGetMessageShareRequest({
  fiatValue,
  currencyValue,
  currencyCode,
  fiatCode,
  userId,
  priceQuotesId,
}: GetMessageProps) {
  const { t } = useTranslation()
  const [link, setLink] = useState<string | null>(null)

  const { createPurchaseOnRampLink, isPending: isCreatingPurchase } =
    useCreatePurchaseOnRampLink({
      onSuccess: (data) => {
        // Extract the link from the response
        if (data && typeof data === 'object' && 'paylink' in data) {
          setLink((data as any).paylink)
        } else if (typeof data === 'string') {
          setLink(data)
        }
      },
      onError: ({ errorMessage, code }) => {
        toast.error(errorMessage || t('api:errors.somethingWrong'))
      },
    })

  // Function to generate link when user clicks share
  const generateLink = async (): Promise<string | null> => {
    if (!userId || !fiatValue || !fiatCode || !currencyCode) {
      toast.error('Missing required information')
      return null
    }

    // Don't generate if already generating
    if (isCreatingPurchase) {
      return link // Return current link if generating
    }

    // Return cached link if exists
    if (link) {
      return link
    }

    try {
      const result = await createPurchaseOnRampLink({
        userId,
        amount: currencyValue,
        fiatCurrency: fiatCode as any, // Type assertion - backend will validate
        cryptoCurrency: currencyCode as CryptoCode,
        paymentMethod: 'Card',
        redirectUrl: 'kemapp://processing-transaction',
        priceQuotesId: priceQuotesId,
      })

      // Extract link from result
      let generatedLink: string | null = null

      if (result && typeof result === 'object' && 'paylink_id' in result) {
        generatedLink = (result as any).paylink_id
      } else if (typeof result === 'string') {
        generatedLink = result
      }

      // Update state for next time
      if (generatedLink) {
        setLink(generatedLink)
      }

      return generatedLink
    } catch (error) {
      return null
    }
  }

  const getMessageWithLink = (linkValue: string | null) => {
    if (linkValue) {
      return `Hi! I'm requesting ${fiatValue} ${fiatCode} (${currencyValue} ${currencyCode}) through Kem. Please send it to me! https://kemapp.io/payment-link/${linkValue}`
    }
    return `Hi! I'm requesting ${fiatValue} ${fiatCode} (${currencyValue} ${currencyCode}) through Kem. Please send it to me! https://kemapp.io/send-request/id=${userId}&currencyCode=${currencyCode}&currencyValue=${currencyValue}`
  }

  return {
    message: getMessageWithLink(link),
    link,
    isLoading: isCreatingPurchase,
    generateLink,
    getMessageWithLink,
  }
}
